import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import FactsheetLogo from '@/common/assets/logo/factsheet_logo.png';

// Step Components
import TermsStep from './steps/TermsStep';
import VerificationStep from './steps/VerificationStep';
import FormStep from './steps/FormStep';
import ResultStep from './steps/ResultStep';
import { useNavigationStore } from '@/common/store';
import { authService, RegisterUserRequest } from '@/services/authService';
import { toastUtils } from '@/common/utils/toastUtils';
import { NaverSAEvents } from '@/common/utils/naverAnalytics';

/**
 * 개인 회원가입 페이지
 * 4단계로 구성: 약관동의 → 본인인증 → 회원정보입력 → 완료
 */

export type SignUpStep = 'terms' | 'verification' | 'form' | 'result';

export interface SignUpData {
  // 본인인증 정보
  name: string;
  phone: string;
  birthGender: string; // 생년월일-성별 (예: 900812-1)
  userDi: string;
  userCi: string;
  token: string;
  
  // 회원정보
  userId: string;
  email: string;
  password: string;
  passwordConfirm: string;
  
  // 약관동의
  termsAgreed: boolean;
  privacyAgreed: boolean;
  marketingAgreed: boolean;
}

const SignUp: React.FC = () => {

  const {  } = useNavigationStore();

  const [currentStep, setCurrentStep] = useState<SignUpStep>('terms');
  const [signUpData, setSignUpData] = useState<SignUpData>({
    name: '',
    phone: '',
    birthGender: '',
    userDi: '',
    userCi: '',
    token: '',
    userId: '',
    email: '',
    password: '',
    passwordConfirm: '',
    termsAgreed: false,
    privacyAgreed: false,
    marketingAgreed: false,
  });

  const handleSignUp = async (data: SignUpData) => {
    const registerUserData: RegisterUserRequest = {
      loginId: data.userId,
      residentNumber: data.birthGender,
      name: data.name,
      phone: data.phone,
      email: data.email,
      password: data.password,
      userDi: data.userDi,
      userCi: data.userCi,
      token: data.token,
    };
    const result = await authService.registerUser(registerUserData);
    if(result?.success) {
      // 네이버 SA 회원가입 전환 추적
      NaverSAEvents.signUp();
      
      await authService.setUserProfile();
      setCurrentStep('result');
    } else {
      toastUtils.error('회원 가입 중 오류가 발생했습니다.');
    }
  };

  /**
   * 다음 단계로 이동
   */
  const handleNextStep = (data: Partial<SignUpData>) => {
    setSignUpData(prev => ({ ...prev, ...data }));
    
    switch (currentStep) {
      case 'terms':
        setCurrentStep('verification');
        break;
      case 'verification':
        setCurrentStep('form');
        break;
      case 'form':
        handleSignUp({...signUpData, ...data});
        
        break;
      default:
        break;
    }
  };

  /**
   * 이전 단계로 이동
   */
  const handlePrevStep = () => {
    switch (currentStep) {
      case 'verification':
        setCurrentStep('terms');
        break;
      case 'form':
        setCurrentStep('verification');
        break;
      case 'result':
        setCurrentStep('form');
        break;
      default:
        break;
    }
  };

  /**
   * 현재 단계에 해당하는 컴포넌트 렌더링
   */
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'terms':
        return (
          <TermsStep
            data={signUpData}
            onNext={handleNextStep}
          />
        );
      case 'verification':
        return (
          <VerificationStep
            data={signUpData}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
          />
        );
      case 'form':
        return (
          <FormStep
            data={signUpData}
            onNext={handleNextStep}
            onPrev={handlePrevStep}
          />
        );
      case 'result':
        return (
          <ResultStep
            data={signUpData}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    
  }, []);


  return (
    <Container>
      <SignUpContainer>
        <SignUpBox>
          <Logo>
            <img src={FactsheetLogo} alt="Factsheet Logo" />
          </Logo>
          
          <StepIndicator>
            <StepItem $active={currentStep === 'terms'} $completed={['verification', 'form', 'result'].includes(currentStep)}>
              <StepNumber $active={currentStep === 'terms'} $completed={['verification', 'form', 'result'].includes(currentStep)}>1</StepNumber>
              <StepText>약관동의</StepText>
            </StepItem>
            <StepConnector $completed={['verification', 'form', 'result'].includes(currentStep)} />
            <StepItem $active={currentStep === 'verification'} $completed={['form', 'result'].includes(currentStep)}>
              <StepNumber $active={currentStep === 'verification'} $completed={['form', 'result'].includes(currentStep)}>2</StepNumber>
              <StepText>본인인증</StepText>
            </StepItem>
            <StepConnector $completed={['form', 'result'].includes(currentStep)} />
            <StepItem $active={currentStep === 'form'} $completed={currentStep === 'result'}>
              <StepNumber $active={currentStep === 'form'} $completed={currentStep === 'result'}>3</StepNumber>
              <StepText>정보입력</StepText>
            </StepItem>
            <StepConnector $completed={currentStep === 'result'} />
            <StepItem $active={currentStep === 'result'} $completed={false}>
              <StepNumber $active={currentStep === 'result'} $completed={false}>4</StepNumber>
              <StepText>완료</StepText>
            </StepItem>
          </StepIndicator>

          {renderCurrentStep()}
        </SignUpBox>
      </SignUpContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100vw;
  height: 100vh;

  background-color: ${semanticColors.background.secondary};  
  box-sizing: border-box;
  overflow-y: auto;
`;

const SignUpContainer = styled.div`
  width: 100%;
  max-width: 500px;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  background-color: ${semanticColors.background.secondary};
`;

const SignUpBox = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    gap: 32px;
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  
  img {
    height: 40px;
    width: auto;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
  max-width: 600px;
  
  @media (max-width: 768px) {
    max-width: 500px;
  }
  
  @media (max-width: 480px) {
    max-width: 400px;
  }
`;

const StepItem = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  opacity: ${props => props.$active || props.$completed ? 1 : 0.4};
  transition: opacity 0.2s ease-in-out;
  flex: 1;
  max-width: 120px;
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 2px;
  background-color: ${props => 
    props.$completed ? semanticColors.background.brand : semanticColors.border.primary
  };
  margin: 0 16px;
  transition: background-color 0.2s ease-in-out;
  
  @media (max-width: 480px) {
    margin: 0 8px;
  }
`;

const StepNumber = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${typography.subtitle.subtitle2}
  font-weight: 600;
  background-color: ${props => 
    props.$completed ? semanticColors.background.brand :
    props.$active ? semanticColors.background.brand : semanticColors.background.primary
  };
  color: ${props => 
    props.$completed || props.$active ? semanticColors.text.white : semanticColors.text.tertiary
  };
  transition: all 0.2s ease-in-out;
  border: 2px solid ${props => 
    props.$completed || props.$active ? semanticColors.background.brand : semanticColors.border.primary
  };
`;

const StepText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  text-align: center;
  font-weight: 500;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export default SignUp;
